import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { HomeView } from "@/views";
import { useAuthStore } from "@/stores/auth";
import { useAdminEfpStore } from "@/stores/adminEfp";

// Types de routes pour meilleure lisibilité
type RouteMeta = {
  layout: "auth" | "dashboard" | "admin";
  isPublicRoute?: boolean;
};

// Routes d'authentification (publiques)
const authRoutes: RouteRecordRaw[] = [
  {
    path: "/remplacement",
    name: "buyer-replacement",
    component: () => import("@/views/ReplacementBuyerView.vue"),
    meta: { isPublicRoute: true },
  },
  {
    path: "/auth",
    name: "auth",
    component: () => import("@/views/auth/SellerAuthView.vue"),
    meta: { layout: "auth", isPublicRoute: true } as RouteMeta,
  },
  {
    path: "/register",
    name: "register",
    component: () => import("@/views/auth/SellerRegisterView.vue"),
    meta: { layout: "auth", isPublicRoute: true } as RouteMeta,
  },
  {
    path: "/reset-password",
    name: "reset-password",
    component: () => import("@/views/auth/ResetPasswordView.vue"),
    meta: { layout: "auth", isPublicRoute: true } as RouteMeta,
  },
];

// Routes Admin EFP
const adminEfpRoutes: RouteRecordRaw[] = [
  {
    path: "/adminEFP",
    redirect: { name: "adminEFP/dashboard" },
    meta: { layout: "dashboard", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/auth",
    name: "adminEFP/auth",
    component: () => import("@/views/adminEFP/auth/AdminEfpAuthView.vue"),
    meta: { layout: "auth", isPublicRoute: true } as RouteMeta,
  },
  {
    path: "/adminEFP/dashboard",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "/adminEFP/dashboard",
        name: "adminEFP/dashboard",
        component: () => import("@/views/adminEFP/AdminEfpImpersonateView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/statistiques",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/statistiques",
        component: () =>
          import("@/views/adminEFP/AdminEfpStatistiquesView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/nouveaux-comptes",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/nouveaux-comptes",
        component: () => import("@/views/adminEFP/AdminEfpNewAccountsView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: 'admin', isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/acheteur-statement",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/acheteur-statement",
        component: () => import("@/views/adminEFP/AdminEfpAcheteurStatementView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/hipay",
    name: "adminEFP/hipay",
    redirect: { name: "adminEFP/nouveaux-comptes", query: { view: "hipay" } },
    meta: { layout: 'admin', isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/vendeur-fiche/:id?",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/vendeur-fiche",
        component: () =>
          import("@/views/adminEFP/AdminEfpSellerFicheView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: 'admin', isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/photos",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "/adminEFP/photos",
        name: "adminEFP/photos",
        component: () => import("@/views/adminEFP/AdminEfpPhotosView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/photos",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "/adminEFP/orders",
        name: "adminEFP/orders",
        component: () => import("@/views/adminEFP/orders/ManageView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/orders/store",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/store-orders",
        component: () =>
          import("@/views/adminEFP/orders/StoreOrdersView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/groupages",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/groupages",
        component: () => import("@/views/adminEFP/orders/GroupageView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/groupage-detail/:id",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/groupage-detail",
        component: () =>
          import("@/views/adminEFP/orders/GroupageDetailView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/mel-template",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/mel-template",
        component: () => import("@/views/adminEFP/AdminEfpMelTemplateView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false },
  },
  {
    path: "/adminEFP/couleurs",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/couleurs",
        component: () => import("@/views/adminEFP/AdminEfpCouleursView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/code-promos",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/code-promos",
        component: () =>
          import("@/views/adminEFP/code-promos/AdminEfpCodePromosView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/facturation",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/facturation",
        component: () =>
          import("@/views/adminEFP/facturation/AdminEfpFacturationView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/vendeur-facturation",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/vendeur-facturation",
        component: () =>
          import(
            "@/views/adminEFP/vendeur-facturation/AdminEfpVendeurFacturationView.vue"
          ),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/vendeur-facturation/recap/:idVendeur",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/vendeur-facturation-recap",
        component: () =>
          import(
            "@/views/adminEFP/vendeur-facturation/AdminEfpVendeurFacturationRecapView.vue"
          ),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/commandes-rupture",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/commandes-rupture",
        component: () => import("@/views/adminEFP/ruptures/RupturesView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/store-prestations",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/store-prestations",
        component: () =>
          import("@/views/adminEFP/AdminStorePrestationsView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/actualites",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/actualites",
        component: () => import("@/views/adminEFP/AdminEfpActualitesView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/cms/pages",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/cms/pages",
        component: () => import("@/views/adminEFP/AdminCmsView.vue"),
        props: { resource: "page" },
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/cms/widgets",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/cms/widgets",
        component: () => import("@/views/adminEFP/AdminCmsView.vue"),
        props: { resource: "widget" },
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/cms/media",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/cms/media",
        component: () => import("@/views/adminEFP/AdminCmsMediaView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/shooting/modeles",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "/adminEFP/shooting/modeles",
        name: "list-modeles",
        component: () => import("@/views/adminEFP/shooting/ListModeles.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/shooting",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "/adminEFP/shooting/modeles/:id",
        name: "edit-modele",
        component: () => import("@/views/adminEFP/shooting/EditModele.vue"),
      },
      {
        path: "/adminEFP/shooting/modeles/add",
        name: "add-modele",
        component: () => import("@/views/adminEFP/shooting/AddModele.vue"),
      },
      {
        path: "/adminEFP/shooting/planning/:id",
        name: "planning-modele",
        component: () =>
          import("@/views/adminEFP/shooting/ShootingPlanning.vue"),
      },
      {
        path: "/adminEFP/shooting/agenda",
        name: "agenda-shooting",
        component: () => import("@/views/adminEFP/shooting/AgendaShooting.vue"),
      },
      {
        path: "/adminEFP/shooting/agenda/jour/:date",
        name: "agenda-shooting-jour",
        component: () =>
          import("@/views/adminEFP/shooting/AgendaShootingJour.vue"),
      },
      {
        path: "/adminEFP/shooting/manager-planning",
        name: "manager-shooting",
        component: () =>
          import("@/views/adminEFP/shooting/ManagerShooting.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/shopify",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/shopify",
        component: () =>
          import("@/views/adminEFP/shooting/AdminShopifyPrivacyView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/litige-retour",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/litige-retour",
        component: () => import("@/views/adminEFP/LitigeRetourManagement.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/top-acheteurs",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/top-acheteurs",
        component: () =>
          import("@/views/adminEFP/AdminEfpTopAcheteursView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/nouveaux-acheteurs",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/nouveaux-acheteurs",
        component: () =>
          import("@/views/adminEFP/AdminEfpNouveauxAcheteursView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/grossistes-a-recontacter",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/grossistes-a-recontacter",
        component: () =>
          import("@/views/adminEFP/AdminEfpGrossistesARecontacterView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/pickupreturn",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "/adminEFP/pickupreturn",
        name: "pickup-return",
        component: () => import("@/views/adminEFP/PickupAndReturnView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/access",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/access",
        component: () =>
          import("@/views/adminEFP/AdminAccessManagementView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/logging",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/logging",
        component: () => import("@/views/adminEFP/AdminAuditLogsView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
  {
    path: "/adminEFP/no-access",
    component: () => import("@/layout/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "adminEFP/no-access",
        component: () => import("@/views/adminEFP/AdminNoAccessView.vue"),
      },
    ],
    beforeEnter: createAdminEfpGuard(),
    meta: { layout: "admin", isPublicRoute: false } as RouteMeta,
  },
];

// Routes du dashboard (layout général)
const dashboardRoutes: RouteRecordRaw[] = [
  // Dashboard principal
  {
    path: "/dashboard",
    component: () => import("@/layout/LayoutGeneral.vue"),
    meta: { layout: "dashboard", isPublicRoute: false } as RouteMeta,
    children: [
      {
        path: "",
        name: "dashboard",
        component: HomeView,
      },
    ],
  },

  // Compte utilisateur
  {
    path: "/account",
    component: () => import("@/layout/LayoutGeneral.vue"),
    meta: { layout: "dashboard", isPublicRoute: false } as RouteMeta,
    children: [
      {
        path: "infos",
        name: "account/infos",
        component: () =>
          import("@/views").then((m) => m.GeneralInformationView),
      },
      {
        path: "sales",
        name: "account/sales",
        component: () => import("@/views").then((m) => m.MySalesView),
      },
      {
        path: "discount",
        name: "account/discount",
        component: () => import("@/views").then((m) => m.MyDiscountView),
      },
      {
        path: "promocode",
        name: "account/promocode",
        component: () => import("@/views").then((m) => m.PromoCodeView),
      },
      {
        path: "brands",
        name: "account/brands",
        component: () => import("@/views").then((m) => m.MyBrandsView),
      },
      {
        path: "promotionalemail",
        name: "account/promotionalemail",
        beforeEnter: () => ({ name: "account/infos" }),
        component: () => import("@/views").then((m) => m.PromotionalEmailView),
      },
      {
        path: "qrcode",
        name: "account/qrcode",
        beforeEnter: () => ({ name: "account/infos" }),
        component: () => import("@/views").then((m) => m.GenerateQrcodeView),
      },
      {
        path: "beuinfo",
        name: "account/beuinfo",
        component: () => import("@/views").then((m) => m.BeuInfoView),
      },
    ],
  },

  // Catalogue
  {
    path: "/catalog",
    component: () => import("@/layout/LayoutGeneral.vue"),
    meta: { layout: "dashboard", isPublicRoute: false } as RouteMeta,
    children: [
      {
        path: "listmel",
        name: "catalog/listmel",
        component: () => import("@/views").then((m) => m.ListMelView),
      },
      {
        path: "meaproduct",
        name: "catalog/meaproduct",
        component: () => import("@/views").then((m) => m.MeaProductView),
      },
      {
        path: "mel",
        name: "catalog/mel",
        component: () => import("@/views").then((m) => m.MelView),
      },
      {
        path: "shooting-request",
        name: "catalog/shooting-request",
        component: () => import("@/views").then((m) => m.MelView),
      },
      {
        path: "packagemanagement",
        name: "catalog/packagemanagement",
        component: () => import("@/views").then((m) => m.PackageManagementView),
      },
      {
        path: "productlist",
        name: "catalog/productlist",
        component: () => import("@/views").then((m) => m.ProductListView),
      },
      {
        path: "sizerange",
        name: "catalog/sizerange",
        component: () => import("@/views").then((m) => m.SizeRangeView),
      },
      {
        path: "uploadphoto",
        name: "catalog/uploadphoto",
        beforeEnter: createMelGuard(),
        component: () => import("@/views").then((m) => m.UploadPhotoView),
      },
    ],
  },

  // Prestations
  {
    path: "/prestationproducts",
    component: () => import("@/layout/LayoutGeneral.vue"),
    meta: { layout: "dashboard", isPublicRoute: false } as RouteMeta,
    children: [
      {
        path: "",
        name: "prestationproducts",
        component: () =>
          import("@/views").then((m) => m.PrestationProductsView),
      },
    ],
  },

  // Commandes
  {
    path: "/orders",
    component: () => import("@/layout/LayoutGeneral.vue"),
    redirect: { name: "myorders" },
    meta: { layout: "dashboard", isPublicRoute: false } as RouteMeta,
    children: [
      {
        path: "",
        name: "myorders",
        component: () => import("@/views").then((m) => m.MyOrdersView),
      },
      {
        path: "facture",
        name: "facture",
        component: () => import("@/views").then((m) => m.FactureView),
      },
      {
        path: "litiges-retours",
        name: "my-litiges-retours",
        component: () =>
          import("@/views").then((m) => m.MyLitigesRetoursView),
      },
    ],
  },

  // Détails commande
  {
    path: "/orderdetails",
    component: () => import("@/layout/LayoutGeneral.vue"),
    meta: { layout: "dashboard", isPublicRoute: false } as RouteMeta,
    children: [
      {
        path: "",
        name: "orderdetails",
        component: () => import("@/views").then((m) => m.OrderDetailsView),
      },
      {
        path: ":id",
        name: "orderdetails-detail",
        component: () => import("@/views").then((m) => m.OrderDetailsView),
      },
    ],
  },

  // Modules
  {
    path: "/modules",
    component: () => import("@/layout/LayoutGeneral.vue"),
    meta: { layout: "dashboard", isPublicRoute: false } as RouteMeta,
    children: [
      {
        path: "lookbook",
        name: "modules/lookbook",
        component: () =>
          import("@/views").then((m) => m.LookbookManagementView),
      },
      {
        path: "buyers",
        name: "modules/buyers",
        component: () => import("@/views").then((m) => m.BuyerManagementView),
      },
    ],
  },

  // Services
  {
    path: "/services",
    component: () => import("@/layout/LayoutGeneral.vue"),
    meta: { layout: "dashboard", isPublicRoute: false } as RouteMeta,
    children: [
      {
        path: "",
        name: "services",
        component: () => import("@/views").then((m) => m.ServicesView),
      },
      {
        path: "prestashop",
        name: "services/prestashop",
        component: () =>
          import("@/views").then((m) => m.PrestashopImportView),
      },
      {
        path: "shopify",
        name: "services/shopify",
        component: () =>
          import("@/views").then((m) => m.ShopifyImportView),
      },
    ],
  },
];

// Routes racine
const rootRoutes: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: "/dashboard",
    meta: { layout: "dashboard", isPublicRoute: false } as RouteMeta,
  },
];

// Fusion de toutes les routes
const routes: RouteRecordRaw[] = [
  ...rootRoutes,
  ...authRoutes,
  ...adminEfpRoutes,
  ...dashboardRoutes,
];

// Guards factories
function createAdminEfpGuard() {
  return async (to: any, _from: any, next: any) => {
    const adminEfp = useAdminEfpStore();
    const ok = await adminEfp.validate();
    if (!ok) {
      next({ name: "adminEFP/auth" });
      return;
    }
    if (!adminEfp.canAccessPath(to.path)) {
      next({ path: adminEfp.firstAccessiblePath(), replace: true });
      return;
    }
    next();
  };
}

function createMelGuard() {
  return (to: any, from: any, next: any) => {
    const authStore = useAuthStore();

    // Vérifier mel depuis authStore.user ou localStorage
    let mel: string | number | null = null;
    if (authStore.user?.mel !== undefined) {
      mel = authStore.user.mel;
    } else {
      mel = authStore.getMelFromStorage();
    }

    if (mel === "1" || mel === 1 || mel === "2" || mel === 2) {
      next();
    } else {
      next({ name: "catalog/listmel" });
    }
  };
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0, behavior: "smooth" };
  },
});

// Auth guard global
router.beforeEach(async (to) => {
  if (to.name === "buyer-replacement") return true;
  const authStore = useAuthStore();
  const isFreshImpersonation = to.query._imp === "1";

  // Déterminer si la route est publique
  const isPublicRoute =
    to.meta.isPublicRoute === true ||
    to.path.startsWith("/adminEFP/auth") ||
    to.name === "adminEFP/auth";

  if (isFreshImpersonation && !to.path.startsWith("/adminEFP")) {
    authStore.resetForImpersonation();
  }

  // Statut d'impersonation
  if (!isPublicRoute && !to.path.startsWith("/adminEFP")) {
    try {
      const { adminEfpImpersonationStatus } = await import("@/api/adminEfp");
      const status = await adminEfpImpersonationStatus();
      authStore.setImpersonationFromCookie(
        status.active,
        status.adminDisplayName ?? undefined
      );
    } catch {
      authStore.setImpersonationFromCookie(false);
    }
  }

  // Initialisation auth si nécessaire
  if (
    !to.path.startsWith("/adminEFP") &&
    (!authStore.isInitialized || isFreshImpersonation)
  ) {
    try {
      await authStore.checkAuth({ force: isFreshImpersonation });
    } catch (_) {
      // Ignorer
    }
  }

  // Impersonation admin : retenter l'auth vendeur avant d'afficher une page protégée
  const isProtectedRoute = !isPublicRoute && !to.path.startsWith("/adminEFP");
  if (
    isProtectedRoute &&
    authStore.isImpersonating &&
    !authStore.isAuthenticated
  ) {
    try {
      await authStore.checkAuth({ force: true });
    } catch (_) {
      // Ignorer
    }
    if (!authStore.isAuthenticated) {
      return { path: "/adminEFP", replace: true };
    }
  }

  // Redirection si non authentifié sur route protégée
  if (
    isProtectedRoute &&
    !authStore.isAuthenticated &&
    !authStore.token &&
    !authStore.isImpersonating
  ) {
    // Vérifier si l'admin EFP est déjà authentifié (session cookie)
    const adminEfp = useAdminEfpStore();
    const isAdminEfpAuth =
      adminEfp.isAdminEfpValidated || (await adminEfp.validate());
    if (!isAdminEfpAuth) {
      return {
        path: "/auth",
        query: { redirect: to.fullPath },
      };
    }
  }

  // Redirection si authentifié sur route publique
  if (authStore.isAuthenticated && isPublicRoute) {
    return { path: "/dashboard", replace: true };
  }

  if (isFreshImpersonation) {
    const { _imp: _removed, ...restQuery } = to.query;
    return { ...to, query: restQuery, replace: true };
  }

  return true;
});

export default router;
